import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { FileText, Calendar, Clock, ArrowRight, TrendingUp, Building, DollarSign, PiggyBank, Shield, GraduationCap, BarChart, Brain, Calculator, BookOpen, Users, Target, ChevronDown, Briefcase, LineChart, Landmark, CreditCard, Coins, Award, Scale, Wallet, Globe, Percent, BadgeDollarSign } from 'lucide-react';
import blogPostsData from '../data/blogPosts';
import additionalBlogPosts from '../data/additionalBlogPosts';

// Combine all blog posts
const allBlogPosts = [...blogPostsData, ...additionalBlogPosts];

// Icon mapping
const iconMap = {
  GraduationCap, FileText, Users, BarChart, DollarSign, Target, Brain, Shield,
  LineChart, Scale, Landmark, Calculator, Briefcase, Globe, Percent, Award,
  Building, TrendingUp, PiggyBank, Coins, CreditCard, Wallet, BadgeDollarSign, BookOpen
};

const Blog = () => {
  const [visiblePosts, setVisiblePosts] = useState(12);
  const [activeCategory, setActiveCategory] = useState('All Posts');

  const categories = [
    { name: 'All Posts', count: allBlogPosts.length },
    { name: 'IFC Exam Prep', count: allBlogPosts.filter(p => p.category === 'IFC Exam Prep').length },
    { name: 'CSC Exam Prep', count: allBlogPosts.filter(p => p.category === 'CSC Exam Prep').length },
    { name: 'CIRO Exam Prep', count: allBlogPosts.filter(p => p.category === 'CIRO Exam Prep').length },
    { name: 'Investing in Canada', count: allBlogPosts.filter(p => p.category === 'Investing in Canada').length },
    { name: 'Real Estate', count: allBlogPosts.filter(p => p.category === 'Real Estate').length },
    { name: 'Financial Planning', count: allBlogPosts.filter(p => p.category === 'Financial Planning').length },
    { name: 'AI Finance', count: allBlogPosts.filter(p => p.category === 'AI Finance').length },
    { name: 'CFA Prep', count: allBlogPosts.filter(p => p.category === 'CFA Prep').length },
    { name: 'Career Advice', count: allBlogPosts.filter(p => p.category === 'Career Advice').length },
  ];

  const filteredPosts = activeCategory === 'All Posts' 
    ? allBlogPosts 
    : allBlogPosts.filter(post => post.category === activeCategory);

  const displayedPosts = filteredPosts.slice(0, visiblePosts);

  // Structured data for blog
  const blogSchema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "Harry Vadalkar Finance Blog - IFC, CSC, CIRO Exam Prep & Canadian Investing",
    "description": "Expert Canadian finance blog with articles covering IFC exam prep, CSC course tips, CIRO certification, TFSA strategies, RRSP planning, real estate investing, AI-powered financial analysis, CFA prep, and career advice for finance professionals.",
    "url": "https://harryvadalkar.ca/blog",
    "author": {
      "@type": "Person",
      "name": "Harry Vadalkar"
    },
    "blogPost": allBlogPosts.slice(0, 20).map(post => ({
      "@type": "BlogPosting",
      "headline": post.title,
      "description": post.excerpt,
      "datePublished": post.date,
      "author": {
        "@type": "Person",
        "name": "Harry Vadalkar"
      }
    }))
  };

  return (
    <div className="min-h-screen" data-testid="blog-page">
      <Helmet>
        <title>Canadian Finance Blog | IFC Exam Tips | CSC Study Guide | CIRO Prep | TFSA RRSP Strategies | CFA Prep | Harry Vadalkar</title>
        <meta name="description" content="Expert Canadian finance blog with articles on IFC exam preparation tips, CSC study guides, CIRO certification advice, TFSA strategies, RRSP optimization, real estate investing, AI financial modeling, CFA prep, and career advice. Free educational content from Harvard & MIT certified instructor." />
        <meta name="keywords" content="IFC exam blog, IFC exam tips, IFC study guide, IFC mock exams, IFC question bank, CSC exam blog, CSC exam tips, CSC level 1, CSC level 2, CIRO exam prep, CIRE exam, RSE exam, Canadian investing blog, TFSA strategies, RRSP tips, real estate investing Canada, financial planning Canada, CFA derivatives, Harry Vadalkar blog" />
        <link rel="canonical" href="https://harryvadalkar.ca/blog" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://harryvadalkar.ca/blog" />
        <meta property="og:title" content="Canadian Finance Blog | Expert Articles on IFC, CSC, CIRO & Investing" />
        <meta property="og:description" content="Free Canadian finance education. IFC exam tips, CSC study guides, CIRO prep, TFSA/RRSP strategies, real estate investing, CFA prep, and career advice." />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Canadian Finance Blog | Harry Vadalkar" />
        <meta name="twitter:description" content="Free Canadian finance education. IFC exam tips, CSC study guides, CIRO prep, TFSA/RRSP strategies." />
        <script type="application/ld+json">
          {JSON.stringify(blogSchema)}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-900/20 to-[#020617]" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-blue-500/20 text-blue-400 px-4 py-2 rounded-full mb-6">
            <FileText className="w-4 h-4" />
            <span className="font-medium">{allBlogPosts.length}+ Expert Articles</span>
          </div>
          <h1 className="font-heading text-5xl md:text-6xl font-bold text-white leading-tight mb-6">
            Canadian Finance <span className="text-blue-400">Blog</span>
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Expert insights on IFC exam prep, CSC certification, CIRO licensing, TFSA/RRSP strategies, real estate investing, CFA preparation, and AI-powered finance. Free educational content for Canadian investors and finance professionals.
          </p>
        </div>
      </section>

      {/* Blog Content */}
      <section className="py-20" data-testid="blog-posts">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-4 gap-8">
            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="glass rounded-xl p-6 sticky top-24">
                <h3 className="font-semibold text-white mb-4">Categories</h3>
                <ul className="space-y-2">
                  {categories.map((category, index) => (
                    <li key={index}>
                      <button
                        onClick={() => {
                          setActiveCategory(category.name);
                          setVisiblePosts(12);
                        }}
                        className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-all text-left ${
                          activeCategory === category.name
                            ? 'bg-blue-500/20 text-blue-400'
                            : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
                        }`}
                      >
                        <span className="text-sm">{category.name}</span>
                        <span className="text-xs opacity-60">{category.count}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Posts Grid */}
            <div className="lg:col-span-3">
              <div className="grid md:grid-cols-2 gap-6">
                {displayedPosts.map((post, index) => {
                  const IconComponent = iconMap[post.icon] || BookOpen;
                  return (
                    <article
                      key={index}
                      className="glass rounded-xl overflow-hidden hover:border-blue-500/30 transition-all duration-300 group flex flex-col"
                    >
                      <div className="p-6 flex-grow">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="p-2 rounded-lg bg-blue-500/20">
                            <IconComponent className="w-5 h-5 text-blue-400" />
                          </div>
                          <span className="text-blue-400 text-sm font-medium">{post.category}</span>
                        </div>
                        
                        <Link to={`/blog/${post.slug}`}>
                          <h2 className="font-heading text-lg font-bold text-white mb-3 group-hover:text-blue-400 transition-colors line-clamp-2">
                            {post.title}
                          </h2>
                        </Link>
                        <p className="text-slate-400 text-sm mb-4 line-clamp-3">{post.excerpt}</p>
                        
                        <div className="flex items-center gap-4 text-xs text-slate-500 mb-4">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {post.date}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {post.readTime}
                          </span>
                        </div>
                      </div>
                      
                      {/* CTA Buttons */}
                      <div className="px-6 pb-6 flex gap-2">
                        <Link
                          to={`/blog/${post.slug}`}
                          className="flex-1 inline-flex items-center justify-center bg-slate-700 hover:bg-slate-600 text-white font-medium px-4 py-2 rounded-lg transition-all text-sm"
                        >
                          Read Article
                        </Link>
                        <a
                          href={post.cta.link}
                          target={post.cta.link.startsWith('http') ? '_blank' : '_self'}
                          rel={post.cta.link.startsWith('http') ? 'noopener noreferrer' : ''}
                          className="flex-1 inline-flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2 rounded-lg transition-all text-sm"
                        >
                          {post.cta.text}
                          <ArrowRight className="w-4 h-4 ml-1" />
                        </a>
                      </div>
                    </article>
                  );
                })}
              </div>

              {/* Load More Button */}
              {visiblePosts < filteredPosts.length && (
                <div className="text-center mt-12">
                  <button
                    onClick={() => setVisiblePosts(prev => prev + 12)}
                    className="inline-flex items-center bg-slate-700 hover:bg-slate-600 text-white font-semibold px-8 py-4 rounded-full transition-all"
                  >
                    Load More Articles
                    <ChevronDown className="w-5 h-5 ml-2" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900/30" data-testid="blog-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to <span className="text-blue-400">Start Learning</span>?
          </h2>
          <p className="text-slate-300 text-lg mb-8">
            Join 30,000+ students mastering Canadian finance with our comprehensive courses.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="/courses"
              className="inline-flex items-center bg-blue-600 hover:bg-blue-700 text-white font-bold px-8 py-4 rounded-full transition-all"
            >
              View All Courses
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
            <a
              href="/youtube"
              className="inline-flex items-center bg-red-600 hover:bg-red-700 text-white font-bold px-8 py-4 rounded-full transition-all"
            >
              Join YouTube Membership
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blog;
